import React, { useState } from 'react';
import { useFormField } from '../../shared/forms/useFormField';
import type { StripControlledProperties } from '../../shared/forms/types';
import { ComboBoxField, ComboBoxItem, type ComboBoxFieldProps } from '../../shared/forms/controls/base-fields/ComboBoxField';
import { useQuery } from '@tanstack/react-query';
import type { Key } from 'react-aria-components';
import { getConceptOptions, searchBodyStructuresOptions, type SearchSnowstormConcept } from '@generated';

type Status = 'loadingInitial' | 'initialLoaded' | 'empty' | 'searching' | 'hasOptions' | 'hasValue' | 'error';

export interface BodyStructureSearchFormFieldProps
	extends StripControlledProperties<Omit<ComboBoxFieldProps<SearchSnowstormConcept>, 'value' | 'children' | 'required'>> {}

export function BodyStructureSearchFormField(props: BodyStructureSearchFormFieldProps) {
	const fieldProps = useFormField<string | null>(props);
	const [searchState, setSearchState] = useState<{ currentStatus: Status; searchTerm: string; inputValue: string }>({
		currentStatus: fieldProps.value ? 'loadingInitial' : 'empty',
		searchTerm: '',
		inputValue: ''
	});

	const deferredSearchTerm = React.useDeferredValue(searchState.searchTerm);

	const {
		data: initialData,
		isLoading: initialDataIsLoading,
		isError: initialDataError
	} = useQuery({
		...getConceptOptions(fieldProps.value!),
		enabled: searchState.currentStatus === 'loadingInitial',
		staleTime: Infinity,
		gcTime: Infinity
	});

	const { data, isLoading, isError } = useQuery({
		...searchBodyStructuresOptions(deferredSearchTerm),
		enabled: deferredSearchTerm.length >= 3,
		staleTime: Infinity,
		gcTime: Infinity,
		placeholderData: prev => prev
	});

	if (searchState.currentStatus === 'loadingInitial' && !initialDataIsLoading) {
		setSearchState(prev => ({
			...prev,
			currentStatus: initialData ? 'initialLoaded' : 'error',
			inputValue: initialData ? initialData.label : ''
		}));
	} else if (searchState.currentStatus === 'searching') {
		setSearchState(prev => ({ ...prev, currentStatus: isError ? 'error' : data && data.length > 0 ? 'hasOptions' : 'empty' }));
	}

	const onChange = (selected: Key | null) => {
		if (!selected) {
			fieldProps.onChange(null);
			setSearchState({ currentStatus: 'empty', searchTerm: '', inputValue: '' });
		} else if (data) {
			const selectedOption = data.find(i => i.id === selected.toString())!;
			fieldProps.onChange(selected.toString());
			setSearchState({ currentStatus: 'hasValue', searchTerm: selectedOption.label, inputValue: selectedOption.label });
		} else {
			throw new Error('Selected value not found in options');
		}
	};

	if (searchState.currentStatus === 'loadingInitial') {
		return <p>Loading...</p>;
	}

	return (
		<ComboBoxField
			{...props}
			label={props.label ?? 'Body Structure'}
			hideChevron
			selectedKey={fieldProps.value}
			onSelectionChange={onChange}
			items={searchState.currentStatus === 'hasOptions' ? data : []}
			inputValue={searchState.inputValue}
			onInputChange={(value: string) => setSearchState({ currentStatus: 'searching', searchTerm: value, inputValue: value })}
			allowsEmptyCollection
		>
			{item => <ComboBoxItem id={item.id}>{item.label}</ComboBoxItem>}
		</ComboBoxField>
	);
}
